import React, { useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import ChatMark from './ChatMark';
import ChatHome from './ChatHome';
import ChatThread from './ChatThread';
import ChatComposer from './ChatComposer';
import ChatStartForm from './ChatStartForm';
import tokens from '../theme/tokens';

// 실시간문의 패널 — 채널톡처럼 오른쪽 아래에 뜨는 창(모바일은 전체 화면).
//   홈: 로고·제목 머리 + ChatHome(인사·새 문의·나의 문의·다른 방법)
//   문의: [←] 제목·상태 머리 + 대화 + (비회원 시작 폼 | 입력창)
const INTRO = '문의를 남겨 주시면 담당자가 확인 후 바로 답변드립니다.';

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    right: 24,
    zIndex: tokens.zPanel,
    display: 'flex',
    flexDirection: 'column',
    width: 380,
    height: 'min(640px, calc(100vh - 140px))',
    borderRadius: tokens.radiusLg,
    backgroundColor: tokens.surface,
    boxShadow: '0 12px 40px rgba(22, 22, 23, 0.18)',
    overflow: 'hidden',
    animation: '$pop .2s ease-out',
    transformOrigin: 'bottom right',
  },
  full: {
    inset: 0,
    right: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
    animation: '$slide .22s ease-out',
    transformOrigin: 'bottom center',
  },
  '@keyframes pop': {
    from: { opacity: 0, transform: 'scale(.96) translateY(8px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' },
  },
  '@keyframes slide': {
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  head: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    minHeight: 68,
    padding: '12px 12px 12px 16px',
    backgroundImage: 'linear-gradient(to right, #ff925c 0%, #f77c40 100%)',
    color: tokens.surface,
  },
  mark: { marginRight: 12 },
  iconBtn: {
    flex: 'none',
    width: 36,
    height: 36,
    borderRadius: 18,
    color: tokens.surface,
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
  },
  back: { marginRight: 6, marginLeft: -6 },
  titles: { flex: '1 1 auto', minWidth: 0 },
  title: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: -0.8,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sub: {
    marginTop: 4,
    fontSize: 12,
    opacity: 0.92,
    letterSpacing: -0.4,
  },
  icon: { width: 18, height: 18 },
}));

function ChatPanel({ full = false, bottom = 96, chat, onClose }) {
  const cs = useStyles();
  const { view, activeRoom, activeId, rooms, messages, hasMore, loading, sending, error, connected, needsContact } = chat;

  // 전체 화면(모바일)에서는 뒤 페이지가 스크롤되지 않게
  useEffect(() => {
    if (!full) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [full]);

  const inRoom = view === 'room';
  const ended = Boolean(activeRoom) && activeRoom.status === 'ended';
  const roomTitle = activeRoom ? (activeRoom.title || `문의 ${activeRoom.seq}`) : '새 문의';
  const roomSub = ended ? '상담 종료 · 다시 보내면 새 상담으로 이어집니다' : connected ? '온라인' : '연결 중';

  return (
    <div className={clsx(cs.root, full && cs.full)} style={full ? undefined : { bottom }} role="dialog" aria-label="퀴노스 실시간문의">
      <div className={cs.head}>
        {inRoom ? (
          <ButtonBase className={clsx(cs.iconBtn, cs.back)} onClick={chat.goHome} aria-label="문의 목록으로" focusRipple={false}>
            <svg className={cs.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </ButtonBase>
        ) : (
          <ChatMark className={cs.mark} size={40} />
        )}
        <div className={cs.titles}>
          <div className={cs.title}>{inRoom ? roomTitle : '채팅'}</div>
          <div className={cs.sub}>{inRoom ? roomSub : (connected ? '온라인' : '연결 중')}</div>
        </div>
        <ButtonBase className={cs.iconBtn} onClick={onClose} aria-label="닫기" focusRipple={false}>
          <svg className={cs.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </ButtonBase>
      </div>

      {!inRoom && <ChatHome rooms={rooms} onNew={chat.startNew} onOpen={chat.openRoom} />}

      {inRoom && (
        <React.Fragment>
          <ChatThread messages={messages} hasMore={hasMore} loading={loading} onLoadMore={chat.loadMore} intro={activeId ? INTRO : ''} />
          {needsContact
            ? <ChatStartForm onStart={chat.startAsGuest} />
            : <ChatComposer onSend={chat.send} onTyping={chat.typing} sending={sending} error={error} draft={chat.draft} onDraftUsed={chat.clearDraft} />}
        </React.Fragment>
      )}
    </div>
  );
}

export default ChatPanel;
